import {gql} from '@apollo/client';
import {Alert} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {BioentitySummary} from 'folder-items/FileConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import AnalysisTemplatesEditableField from 'instruments/analysis-templates/AnalysisTemplatesEditableField';
import * as ConnectFilesDAO from 'instruments/connections/ConnectFilesDAO';
import {
  InstrumentServiceDetails,
  ConnectionStatusResponse,
  ConnectionStatus,
  ConnectionFieldType,
  AdapterField,
  VALIDATE_CREDENTIALS_COMMAND,
} from 'instruments/connections/ConnectionConstants';
import {
  bulkArchive,
  bulkUnarchive,
  getAdapter,
  getInstrumentServiceDetails,
  startCredentialTest,
  updateConnection,
} from 'instruments/connections/ConnectionDAO';
import ConnectionFieldsForm from 'instruments/connections/ConnectionFieldsForm';
import {
  buildAdapterFields,
  getInitialValues,
  applyAutoSyncLogic,
} from 'instruments/connections/ConnectionFieldsUtils';
import ConnectionStatusDisplay from 'instruments/connections/ConnectionStatusDisplay';
import {DatalakeExportModal} from 'instruments/connections/DatalakeExportModal';
import {EquipmentEditableField} from 'instruments/equipment/EquipmentPicker';
import {
  InstrumentQuery,
  InstrumentQueryStatus,
  isDatabricksAdapter,
  isSnowflakeAdapter,
  isTableExportAdapter,
} from 'instruments/instrument-data/InstrumentDataConstants';
import {pollInstrumentQueryStatus} from 'instruments/instrument-data/InstrumentDataDAO';
import {InstrumentGateway, S3_FIELD_IDS} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {getGateway} from 'instruments/instrument-gateways/InstrumentGatewayDAO';
import {GatewayStatusIcon} from 'instruments/instrument-gateways/InstrumentGatewaysPage';
import AliasField from 'metadata/AliasField';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import * as BlobActions from 'schemas/tags/BlobActions';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';
import BlobInputModal from 'schemas/tags/BlobInputModal';
import {ValidationInfo} from 'schemas/tags/EditableSchemaTag';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Title} from 'ui-library/Headers';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import legacyRunModal from 'ui-library/legacyRunModal';

import {ConnectionQuery, ConnectionQueryVariables} from './__generated/ConnectionQuery';
import ConnectionHeaderActions from './ConnectionHeaderActions';

export const CONNECTION_QUERY: DocumentNode = gql`
  query ConnectionQuery($connectionId: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    connection(id: $connectionId) {
      ... on ConnectionNode {
        id
        name
        humanId
        permissions {
          readable
          writable
          archivable
          unarchivable
        }
        adapterId
        creator {
          id
          name
          handle
          avatarUrl
        }
        dataCatalogFolder {
          id
        }
        createdAt
        fields
        ...HasArchiveRecordGraphQL
        associatedEquipment {
          ...BioentitySummaryGraphQL
        }
        analysisTemplates {
          id
          name
          archiveRecord {
            purpose
          }
          configVariables {
            id
            configVariableKey
          }
          dataFrameVariables {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('BioentitySummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type Connection = ExtractByTypename<ConnectionQuery['connection'], 'ConnectionNode'>;

type CredentialTestResult =
  | {status: 'TESTING'}
  | {status: 'VALID'}
  | {status: 'REJECTED'}
  | {status: 'NOT_CHECKED'; reason: string};

// The adapters' info for a login the instrument software rejected, for an instruments service that doesn't say itself
// whether the check's failure was a rejection.
const REJECTED_LOGIN_INFO_PREFIXES = ['Unauthorized', 'Forbidden'];
const CREDENTIAL_FIELD_IDS = ['username', 'password'];
const UNKNOWN_ERROR_REASON = 'An unknown error occurred';

function credentialTestResultFromQuery(query: InstrumentQuery): CredentialTestResult {
  if (query.status === InstrumentQueryStatus.DONE) {
    return {status: 'VALID'};
  }
  const info = query.info ?? '';
  // The service says whether the failure was a rejection, since for some adapters a status like Forbidden can also
  // mean the instrument software couldn't be reached.
  const isRejection =
    (query.values as {credentials_rejected?: boolean} | null)?.credentials_rejected ??
    REJECTED_LOGIN_INFO_PREFIXES.some((prefix) => info.startsWith(prefix));
  return isRejection ? {status: 'REJECTED'} : {status: 'NOT_CHECKED', reason: info || UNKNOWN_ERROR_REASON};
}

export interface ConnectionDetailsProps {
  connection: Connection;
  refetch: () => void;
  refreshDetails: () => void;
  gateway: InstrumentGateway | null;
  status: ConnectionStatusResponse | null;
  adapterDisplayName: string | null;
  adapterFields: Array<AdapterField>;
  // Whether the connection's adapter can check its credentials with VALIDATE_CREDENTIALS.
  supportsCredentialTest: boolean;
  fieldValues: Record<string, string>;
  fieldInfos: {[id: string]: string | null};
  fieldValidationInfos: {[id: string]: ValidationInfo};
  fieldTypes: {[id: string]: string | null};
  allotropyVendor: string | null;
  instrumentServiceDetails: InstrumentServiceDetails | null;
  onClearFieldValidation: (fieldNames: Array<string>) => void;
  onFieldValuesChange: (newValues: Record<string, string>) => void;
}

export function ConnectionDetails(props: ConnectionDetailsProps): JSX.Element {
  const connection = props.connection;
  const connectionId = connection.id;
  const [conversionStatus, setConversionStatus] = useState(null as string | null);
  const [blob, setBlob] = useState<BenchlingBlob | null>(null);
  const [credentialTestResult, setCredentialTestResult] = useState<CredentialTestResult | null>(null);
  // Counts credential tests and credential edits, so a test's result is shown only if neither followed it.
  const credentialTestGeneration = useRef(0);

  const _handleUpdateIsEnabled = async (isEnabled: boolean): Promise<void> => {
    await updateConnection(connectionId, {isEnabled});
    props.refetch();
  };

  const handleFieldChange = async (fieldId: string, value: string): Promise<void> => {
    const updatedValues = applyAutoSyncLogic(fieldId, value, props.fieldValues);

    // Send only the fields that actually changed
    const changedValues: Record<string, string> = {};
    changedValues[fieldId] = value;
    // If auto-sync was disabled, include it too
    if (updatedValues.enable_auto_sync !== props.fieldValues.enable_auto_sync) {
      changedValues.enable_auto_sync = updatedValues.enable_auto_sync;
    }

    // Clear validation for all fields being updated
    props.onClearFieldValidation(Object.keys(changedValues));
    if (CREDENTIAL_FIELD_IDS.includes(fieldId)) {
      credentialTestGeneration.current += 1;
      setCredentialTestResult(null);
    }

    // Update local state with trimmed values to match what's sent to the server
    const adapterFields = buildAdapterFields(changedValues);
    const trimmedValues = {...updatedValues, ...adapterFields};
    props.onFieldValuesChange(trimmedValues);

    await updateConnection(connectionId, {adapterFields});
    props.refreshDetails();
  };

  const _handleUpdateDataCatalogFolderId = async (e: {target: {value: string}}): Promise<void> => {
    await updateConnection(connectionId, {dataCatalogFolderId: e.target.value});
    props.refetch();
  };

  const _handleUpdateEquipment = async (equipment: Array<BioentitySummary>): Promise<void> => {
    await updateConnection(connectionId, {associatedEquipmentIds: equipment.map((item) => item.id)});
    props.refetch();
  };

  const _handleUpdateAnalysisTemplates = async (
    templates: Array<AnalysisTemplateSelectorTemplate>
  ): Promise<void> => {
    await updateConnection(connectionId, {automationTemplateIds: templates.map((template) => template.id)});
    props.refetch();
  };

  const onUploadFiles = (files: Array<File>): JQueryPromise<void> =>
    BlobActions.uploadFilesAsBlobs(files, {
      shouldGeneratePreviewFile: false,
      onBlobsCreated: async (newBlobs) => {
        setConversionStatus('LOADING');
        if (props.allotropyVendor == null || newBlobs.length === 0) {
          return;
        }
        setBlob(newBlobs[0]);
        let blobId = newBlobs[0].id;
        try {
          blobId = (await ConnectFilesDAO.convertToAsm(blobId, props.allotropyVendor)).blobId;
          await ConnectFilesDAO.convertToCsv(blobId);
          setConversionStatus('SUCCESS');
        } catch (err) {
          if (err.status === 400) {
            setConversionStatus('FAILURE');
          } else {
            throw err;
          }
        }
      },
    }).done(() => {});

  const getBlobStatus = (): React.JSX.Element => {
    switch (conversionStatus) {
      case 'SUCCESS':
        return (
          <div>
            <BenchlingIcon
              className={classNames(css(styles.colorGreen), {
                'u-mini-spacing-left': true,
                'u-mini-spacing-right': true,
              })}
              icon="done"
            />
            <span>File is compatible!</span>
          </div>
        );
      case 'FAILURE':
        return (
          <div>
            <BenchlingIcon
              className={classNames(css(styles.colorRedDark), {
                'u-mini-spacing-left': true,
                'u-mini-spacing-right': true,
              })}
              icon="warning"
            />
            <span>File is not compatible.</span>
          </div>
        );
      default:
        return (
          <div>
            <BenchlingIcon
              className={classNames(css(styles.colorGrayDark), {
                'u-mini-spacing-left': true,
                'u-mini-spacing-right': true,
              })}
              icon="hourglass-half"
            />
            <span>Testing...</span>
          </div>
        );
    }
  };

  const renderCredentialTestStatus = (
    icon: BenchlingIconName,
    colorStyle: object,
    message: string
  ): React.JSX.Element => (
    <div>
      <BenchlingIcon
        className={classNames(css(colorStyle), {
          'u-mini-spacing-left': true,
          'u-mini-spacing-right': true,
        })}
        icon={icon}
      />
      <span>{message}</span>
    </div>
  );

  const getCredentialTestStatus = (): React.JSX.Element | null => {
    switch (credentialTestResult?.status) {
      case 'VALID':
        return renderCredentialTestStatus('done', styles.colorGreen, 'Credentials are valid.');
      case 'REJECTED':
        return renderCredentialTestStatus(
          'warning',
          styles.colorRedDark,
          'The instrument software rejected these credentials. Check the username and password.'
        );
      case 'NOT_CHECKED':
        return renderCredentialTestStatus(
          'warning',
          styles.colorRedDark,
          `Couldn't validate credentials: ${credentialTestResult.reason}`
        );
      case 'TESTING':
        return renderCredentialTestStatus('hourglass-half', styles.colorGrayDark, 'Testing credentials...');
      default:
        return null;
    }
  };

  const handleTestCredentials = async (): Promise<void> => {
    credentialTestGeneration.current += 1;
    const generation = credentialTestGeneration.current;
    setCredentialTestResult({status: 'TESTING'});
    const result = await startCredentialTest(connectionId)
      .then(({instrumentQueryId}) => pollInstrumentQueryStatus(instrumentQueryId))
      .then(credentialTestResultFromQuery)
      .catch((error: unknown): CredentialTestResult => {
        // jQuery XHR rejections carry the server's message; polling rejects with an Error once it gives up.
        const serverMessage = (error as {responseJSON?: {userMessage?: string}})?.responseJSON?.userMessage;
        const message = serverMessage ?? (error instanceof Error ? error.message : null);
        return {status: 'NOT_CHECKED', reason: message || UNKNOWN_ERROR_REASON};
      });
    if (generation !== credentialTestGeneration.current) {
      return;
    }
    setCredentialTestResult(result);
    // The instruments service records the result on the username and password fields' validation.
    props.refreshDetails();
  };

  const adapterId = props.instrumentServiceDetails?.adapterId;
  const showTableExport =
    (BenchlingConfig.SNOWFLAKE_GATEWAY_ENABLED && isSnowflakeAdapter(adapterId)) ||
    (BenchlingConfig.DATABRICKS_GATEWAY_ENABLED && isDatabricksAdapter(adapterId));
  const hasCredentialFields =
    props.fieldTypes.username !== undefined && props.fieldTypes.password !== undefined;
  const hasCredentialsForAutoSync = (() => {
    if (props.fieldValues.per_user_login_required !== 'true') {
      return true;
    }
    return Boolean(props.fieldValues.username?.trim() && props.fieldValues.password?.trim());
  })();
  const hasActualCredentials = Boolean(
    props.instrumentServiceDetails?.fields?.username?.value?.trim() &&
    props.instrumentServiceDetails?.fields?.password?.value?.trim()
  );
  const showTestCredentials = props.supportsCredentialTest && hasCredentialFields;

  const permissions = connection.permissions;
  const isWritable = permissions.writable;
  const statusValue = props.status?.value;

  // Surface the actionable validation message from the async CHECK_STATUS probe
  // (e.g. "The public key registered for the Snowflake user does not match...")
  // prominently near the status. The per-field validation icons already carry it,
  // but they're easy to miss, so a failed connection can otherwise look healthy
  // until an export fails. Only fields that finished validating and came back
  // invalid (with a message) are shown.
  //
  // Scoped to Snowflake and Databricks connections: the friendly, actionable messages
  // come from those adapters' CHECK_STATUS classifiers, so this alert is meaningful
  // for them. Other adapters report generic/legacy validation text, and surfacing it
  // prominently for every connection type would be too broad.
  const failedValidationMessages = isTableExportAdapter(adapterId)
    ? Object.values(props.fieldValidationInfos).filter(
        (info): info is ValidationInfo & {validationMsg: string} =>
          info.isValid === false && Boolean(info.validationMsg)
      )
    : [];

  return (
    <div className="react-tab-container">
      <div>
        <div>
          <Row bufferRowReset={true}>
            <Col xs={6}>
              <AliasField
                initialAlias={connection.name}
                isFullWidth={true}
                isHeader={true}
                isWritable={isWritable}
                complete={async (name: string) => {
                  await updateConnection(connectionId, {name});
                  props.refetch();
                }}
                placeholder="Name"
              />
              <ArchiveRecordLabel item={{...connection, archiveRecord: connection.archiveRecord}} />
            </Col>
            <Col xs={6}>
              <div className="u-justify-content-end">
                <ConnectionHeaderActions
                  connectionId={connectionId}
                  connection={connection}
                  isArchivable={Boolean(permissions.archivable)}
                  isUnarchivable={Boolean(permissions.unarchivable)}
                  status={props.status}
                  getAnalyticsMetadata={() => ({
                    actionOrigin: ActionOrigin.CONNECTION_PAGE,
                  })}
                  onArchive={async (ids: Array<string>, purpose: string) => {
                    const {error} = await bulkArchive(ids, purpose);
                    props.refetch();
                    if (error !== undefined) {
                      throw error;
                    }
                  }}
                  onUnarchive={async (ids: Array<string>) => {
                    const {error} = await bulkUnarchive(ids);
                    props.refetch();
                    if (error !== undefined) {
                      throw error;
                    }
                  }}
                  onUpdateIsEnabled={_handleUpdateIsEnabled}
                  onRefreshStatus={props.refreshDetails}
                  onExportTableToDataset={
                    showTableExport
                      ? () =>
                          legacyRunModal(DatalakeExportModal, {
                            connectionId,
                            connectionName: connection.name,
                            sourceName: isDatabricksAdapter(adapterId) ? 'Databricks' : 'Snowflake',
                            onRequestClose: props.refetch,
                          })
                      : undefined
                  }
                />
              </div>
            </Col>
          </Row>
        </div>
        <div>
          <Row bufferRowReset={true}>
            {statusValue ? (
              <Col xs={12}>
                <ConnectionStatusDisplay status={statusValue} />
              </Col>
            ) : null}
          </Row>
          {failedValidationMessages.length > 0 ? (
            <Row bufferRowReset={true}>
              <Col xs={12}>
                <Alert
                  color="red"
                  title="Connection validation failed"
                  className={css(styles.validationAlert)}
                >
                  {failedValidationMessages.length === 1 ? (
                    failedValidationMessages[0].validationMsg
                  ) : (
                    <ul className={css(styles.validationAlertList)}>
                      {failedValidationMessages.map((info) => (
                        <li key={info.name}>{info.validationMsg}</li>
                      ))}
                    </ul>
                  )}
                </Alert>
              </Col>
            </Row>
          ) : null}
        </div>
      </div>
      <div className="u-spacing-top">
        <FormGroup>
          <Row bufferRowReset={true}>
            <Col xs={6}>
              <Title>Creator</Title>
              <div>
                <Avatar
                  className="u-mini-spacing-right"
                  size={AvatarSize.XXSMALL}
                  entity={connection.creator}
                />
                {connection.creator.name}
              </div>
            </Col>
            <Col xs={6}>
              <Title>Type</Title>
              <div>{props.adapterDisplayName}</div>
            </Col>
          </Row>
        </FormGroup>
        <div className="u-spacing-top">
          <FormGroup>
            <Row bufferRowReset={true}>
              <Col xs={6}>
                <Title>Benchling Gateway</Title>
                <div>
                  {props.gateway && <GatewayStatusIcon gateway={props.gateway} />} {props.gateway?.name}
                </div>
              </Col>
              <Col xs={6}>
                <Title>Benchling Location</Title>
                <FolderSelector
                  value={connection.dataCatalogFolder?.id}
                  onChange={_handleUpdateDataCatalogFolderId}
                  showInventory={true}
                  readOnly={!isWritable}
                  unsetOnFetchFail={false}
                />
              </Col>
            </Row>
          </FormGroup>
        </div>
        <div className="u-spacing-top">
          <ConnectionFieldsForm
            fields={props.adapterFields}
            values={props.fieldValues}
            fieldInfos={props.fieldInfos}
            fieldValidationInfos={props.fieldValidationInfos}
            isWritable={isWritable}
            isCreating={false}
            hasCredentialsForAutoSync={hasCredentialsForAutoSync}
            isIamRoleMode={Boolean(props.gateway?.fields?.[S3_FIELD_IDS.CUSTOMER_ROLE_ARN])}
            onChange={handleFieldChange}
          />
        </div>
        {BenchlingConfig.ENABLE_EQUIPMENT && (
          <div className="u-spacing-top">
            <FormGroup>
              <Row bufferRowReset={true}>
                <Col xs={6}>
                  <Title>Associated Equipment</Title>
                  <EquipmentEditableField
                    shouldAllowMultiple={true}
                    initialSelection={props.connection.associatedEquipment.map((item) =>
                      graphQLToREST('BioentitySummary', item)
                    )}
                    onComplete={_handleUpdateEquipment}
                    isWriteable={true}
                  />
                </Col>
              </Row>
            </FormGroup>
          </div>
        )}
        {BenchlingConfig.ENABLE_ANALYSIS_TOOL_TEMPLATES &&
          BenchlingConfig.PIPELINES_ENABLE_CONNECTION_INTEGRATION && (
            <div className="u-spacing-top">
              <FormGroup>
                <Row bufferRowReset={true}>
                  <Col xs={6}>
                    <Title>Automation templates</Title>
                    <AnalysisTemplatesEditableField
                      initialSelection={(props.connection.analysisTemplates || []).map((template) => ({
                        ...template,
                        configVariables: template.configVariables.map((cv) => ({
                          ...cv,
                          name: cv.configVariableKey,
                          variableTypeConfigSpec: {kind: 'string', required: true} as const,
                        })),
                      }))}
                      onComplete={_handleUpdateAnalysisTemplates}
                      isWritable={isWritable}
                    />
                  </Col>
                </Row>
              </FormGroup>
            </div>
          )}
        {showTestCredentials && (
          <FormGroup>
            <Row bufferRowReset={true}>
              <Col sm={12}>
                <FieldLabel label="Test saved credentials" labelStyle={LabelStyle.LABEL} />
                <HelpButton
                  renderContent={() => 'Validate the stored credentials against the adapter'}
                  iconClassName="u-mini-spacing-left"
                />
                <div className={css(styles.button)}>
                  <Tooltip
                    placement={FloatingPlacement.TOP}
                    text="Username and password fields must be set to validate credentials"
                    isDisabled={hasActualCredentials}
                  >
                    <Button
                      buttonStyle={ButtonStyle.TERTIARY_GRAY}
                      size={ButtonSize.MINI}
                      fullWidth={false}
                      text="Test credentials"
                      icon="key"
                      disabled={!hasActualCredentials || credentialTestResult?.status === 'TESTING'}
                      textClassName="button-text--nowrap"
                      onClick={handleTestCredentials}
                    />
                  </Tooltip>
                </div>
                <div className={css(styles.credentialTestStatus)}>{getCredentialTestStatus()}</div>
              </Col>
            </Row>
          </FormGroup>
        )}
        {props.allotropyVendor ? (
          <FormGroup>
            <Row bufferRowReset={true}>
              <Col sm={12}>
                <FieldLabel label="Test file compatibility for Connection" labelStyle={LabelStyle.LABEL} />
                <div className={css(styles.button)}>
                  <Button
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    size={ButtonSize.MINI}
                    fullWidth={false}
                    text="Choose file"
                    icon="upload"
                    disabled={false}
                    textClassName="button-text--nowrap"
                    onClick={() => {
                      legacyRunModal(BlobInputModal, {
                        allowMultiple: false,
                        onUploadFiles,
                      });
                    }}
                  />
                </div>
              </Col>
            </Row>
            {blob ? (
              <Row>
                <Col sm={12}>
                  <div className={css(styles.chip)}>
                    <Chip
                      className="chip-container"
                      icon="paperclip"
                      size={ChipSize.MINI}
                      text={blob.name}
                      href={blob.url}
                      onRemove={() => {
                        setBlob(null);
                        setConversionStatus(null);
                      }}
                    />
                  </div>
                  {getBlobStatus()}
                </Col>
              </Row>
            ) : null}
          </FormGroup>
        ) : null}
      </div>
    </div>
  );
}

const CONNECTION_POLL_INTERVAL_SEC = 10;

export default function QueryingConnectionDetails(props: {connectionId: string}): JSX.Element {
  const connectionId = props.connectionId;
  const result = useBenchlingQuery<ConnectionQuery, ConnectionQueryVariables>(CONNECTION_QUERY, {
    variables: {connectionId},
  });

  const [instrumentServiceDetails, setInstrumentServiceDetails] = useState(
    null as InstrumentServiceDetails | null
  );
  const [gateway, setGateway] = useState(null as InstrumentGateway | null);
  const [adapterDisplayName, setAdapterDisplayName] = useState<string | null>(null);
  const [adapterFields, setAdapterFields] = useState<Array<AdapterField>>([]);
  const [supportsCredentialTest, setSupportsCredentialTest] = useState<boolean>(false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldInfos, setFieldInfos] = useState({} as {[id: string]: string | null});
  const [fieldValidationInfos, setFieldValidationInfos] = useState({} as {[id: string]: ValidationInfo});
  const [fieldTypes, setFieldTypes] = useState({} as {[id: string]: string | null});
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const fetchDetails = useCallback(async (): Promise<void> => {
    try {
      const details = await getInstrumentServiceDetails(connectionId);
      setInstrumentServiceDetails(details);
      const gw = await getGateway(details.gatewayId);
      setGateway(gw);
      const adapter = await getAdapter(details.instrumentName, gw.gatewayType);
      setAdapterDisplayName(details.adapterDisplayName ?? details.instrumentName);
      const displayableFields = adapter.fields.filter(
        (field) =>
          field.fieldType === ConnectionFieldType.TEXT || field.fieldType === ConnectionFieldType.SECRET_REF
      );
      setAdapterFields(displayableFields);
      setSupportsCredentialTest(
        Boolean(adapter.commands?.some((command) => command.id === VALIDATE_CREDENTIALS_COMMAND))
      );
      setFieldValues(getInitialValues(displayableFields, details));
      setFieldInfos(Object.fromEntries(adapter.fields.map((field) => [field.id, field.info])));
      setFieldTypes(
        Object.fromEntries(
          adapter.fields.map((field) => [field.id, field.fieldType ? field.fieldType : null])
        )
      );
      setFieldValidationInfos(
        Object.keys(details.fieldValidationInfoByName).length
          ? details.fieldValidationInfoByName
          : Object.fromEntries(adapter.fields.map((field) => [field.id, {name: field.id}]))
      );
    } finally {
      setHasFetched(true);
    }
  }, [connectionId]);

  // Lightweight poll: only refreshes connection details (status + validation).
  // Gateway and adapter are static and don't need re-fetching.
  const pollDetails = useCallback(async (): Promise<void> => {
    try {
      const details = await getInstrumentServiceDetails(connectionId);
      setInstrumentServiceDetails(details);
      // A connection never validated shows every field pending until the service reports its first validation.
      // Once it has, an empty report means no field has a validation result, e.g. after unchecked credentials.
      setFieldValidationInfos((previous) =>
        Object.keys(details.fieldValidationInfoByName).length ||
        Object.values(previous).some((info) => info.isValid !== undefined)
          ? details.fieldValidationInfoByName
          : previous
      );
    } catch (err) {
      // HTTP errors are expected during transient network issues — the next poll will retry.
      // Rethrow unexpected non-HTTP errors.
      if (!err.status) {
        throw err;
      }
    }
  }, [connectionId]);

  useEffect((): void => {
    fetchDetails();
  }, [fetchDetails]);

  useEffect(() => {
    if (!BenchlingConfig.INSTRUMENTS_ENABLE_CONNECTION_DETAILS_POLLING) {
      return;
    }
    const intervalId = setInterval(pollDetails, CONNECTION_POLL_INTERVAL_SEC * 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [pollDetails]);

  if (result.type !== QueryResultType.SUCCESS) {
    return (
      <div className="react-tab-container">
        <LoadingFileScreen />
      </div>
    );
  }

  const connection = result.data.connection;
  if (isInaccessible(connection)) {
    return (
      <div className="react-tab-container">
        <WorkspaceItemInaccessibleGuard inaccessibleInfo={connection} modelType="connection" />
      </div>
    );
  }

  const status = hasFetched
    ? instrumentServiceDetails?.id != null
      ? instrumentServiceDetails.status
      : {
          value: ConnectionStatus.SERVICE_UNAVAILABLE,
          timestamp: new Date().toISOString(),
        }
    : null;
  return (
    <div className="react-tab-container">
      <ConnectionDetails
        connection={connection}
        refetch={() => {
          fetchDetails();
          result.refetch({connectionId});
        }}
        refreshDetails={pollDetails}
        gateway={gateway}
        status={status}
        adapterDisplayName={adapterDisplayName}
        adapterFields={adapterFields}
        supportsCredentialTest={supportsCredentialTest}
        fieldValues={fieldValues}
        fieldInfos={fieldInfos}
        fieldValidationInfos={fieldValidationInfos}
        fieldTypes={fieldTypes}
        allotropyVendor={instrumentServiceDetails ? instrumentServiceDetails.allotropyVendor || null : null}
        instrumentServiceDetails={instrumentServiceDetails}
        onClearFieldValidation={(fieldNames: Array<string>) => {
          setFieldValidationInfos((prev) => {
            const next = {...prev};
            for (const name of fieldNames) {
              next[name] = {name};
            }
            return next;
          });
        }}
        onFieldValuesChange={setFieldValues}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  button: {
    marginTop: 6,
    flexShrink: 0,
  },
  colorGrayDark: {
    color: Colors.GRAY_DARK,
  },
  colorGreen: {
    color: Colors.GREEN,
  },
  colorRedDark: {
    color: Colors.RED_DARK,
  },
  chip: {
    marginTop: 6,
  },
  credentialTestStatus: {
    marginTop: 8,
  },
  validationAlert: {
    marginTop: 8,
  },
  validationAlertList: {
    margin: 0,
    paddingLeft: 18,
  },
});
